/* OverflowBanner — friendly nudge when a slide has more bullets than its
   layout can fit. Clicking the button spills overflow into a new sub-slide
   for 2 credits and calls `onSpilled` with the response so the parent can
   sync deck state + optionally jump to the new sub-slide via a toast
   action.

   Kept as a small standalone component so the SlideEditor block inside
   PitchEditor.jsx stays scannable — before this refactor the banner
   inline JSX + spill handler were ~90 lines mixed with unrelated
   textarea/photo logic.
*/

import React, { useState } from "react";
import { ArrowsOutSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ORANGE = "#F59E0B";

export default function OverflowBanner({
  deckId,
  slideNum,
  bulletCount,
  threshold = 5,
  onSpilled,
}) {
  const [spilling, setSpilling] = useState(false);
  if (bulletCount <= threshold) return null;
  const overflow = bulletCount - threshold;

  const spillOverflow = async () => {
    if (spilling) return;
    setSpilling(true);
    try {
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/spill-overflow`,
        { keep_count: threshold }
      );
      window.dispatchEvent(new Event("clinq:credits-changed"));
      onSpilled?.(data);
      toast.success(
        `Moved ${data.moved_count} bullet${data.moved_count === 1 ? "" : "s"} to a new sub-slide · ${data.cost_credits} credits used`,
        {
          duration: 8000,
          action: {
            label: "Open sub-slide",
            onClick: () => onSpilled?.({ ...data, __openSubSlide: true }),
          },
        }
      );
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg;
      if (typeof raw === "string") {
        msg = raw;
      } else if (raw && typeof raw === "object") {
        // credits.spend returns 402 with a shaped detail:
        //   { code: 'INSUFFICIENT_CREDITS', message: '...', ...}
        // Surface the message so the founder sees the actual reason
        // (was previously buried behind a generic "Could not create overflow slide").
        msg = raw.message || raw.detail || "Could not create sub-slide.";
      } else {
        msg = "Could not create sub-slide. Please try again.";
      }
      if (!err.isCreditShortage) toast.error(msg);
    } finally {
      setSpilling(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg overflow-hidden"
      data-testid="overflow-banner"
      style={{ backgroundColor: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.35)" }}>
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <div className="rounded-full grid place-items-center"
            style={{ width: 22, height: 22, backgroundColor: "rgba(245, 158, 11, 0.24)" }}>
            <ArrowsOutSimple size={12} weight="bold" style={{ color: ORANGE }} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold" style={{ color: ORANGE }}>
            {overflow} bullet{overflow === 1 ? "" : "s"} won&apos;t fit on this slide
          </div>
          <div className="text-[11.5px] text-white/70 mt-0.5 leading-snug">
            Most photo &amp; illustrated layouts show ~{threshold} bullets. Move the extras into a new sub-slide so nothing gets buried when funders read your deck.
          </div>
        </div>
        <button onClick={spillOverflow}
          disabled={spilling}
          data-testid="spill-overflow-btn"
          className="shrink-0 text-[10.5px] font-bold uppercase tracking-[0.14em] px-3 py-2 rounded inline-flex items-center gap-1.5 disabled:opacity-60 transition-all"
          style={{ backgroundColor: ORANGE, color: "#1a1206" }}
          title="Keep the first N bullets on this slide and move the rest to a new sub-slide">
          <ArrowsOutSimple size={11} weight="bold" />
          {spilling ? "Splitting…" : "Add sub-slide · 2 cr"}
        </button>
      </div>
    </div>
  );
}
